import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError, api, clearStoredToken, errorMessage, markSignedOut } from './api';
import { stopLive } from './live';

export type Role = 'owner' | 'security' | 'cashier' | 'accountant' | 'shop_admin' | 'shop_staff' | 'venue_staff';

export interface Features {
  wallet: boolean;
  otpLogin: boolean;
  loyalty: boolean;
  notifications: boolean;
  customerApp: boolean;
  onlineTopup: boolean;
}

export interface StaffMe {
  user: { id: string; name: string; login: string; role: Role; mustChangePassword?: boolean };
  /** Set when an admin gave this login a password that must be replaced at the next sign-in. */
  mustChangePassword?: boolean;
  permissions: string[];
  /** The extras ticked on top of the role (a subset of permissions; older servers leave it out). */
  grants?: string[];
  /**
   * takes_appointments: the shop keeps an appointment book (a spa or salon; Shops & outlets → "Takes appointments").
   * tracks_inventory: the shop keeps an inventory of raw materials (Shops & outlets → "Inventory of raw materials").
   */
  outlets: { id: string; code: string; name: string; kind: 'parking' | 'shop'; takes_appointments?: boolean; tracks_inventory?: boolean }[];
  features: Features;
  business: { name: string; address?: string | null; timezone: string; supportPhone?: string | null };
  /** Whether the venue charges for parking (setting parking.feesEnabled). Carnival City parks for free. */
  parking?: { feesEnabled: boolean };
}

/** True only when the owner has switched parking charges on: fees, pricing rules and Collect payment then appear. */
export const parkingFeesOn = (me: Pick<StaffMe, 'parking'> | null | undefined) => me?.parking?.feesEnabled === true;

interface StaffCtx {
  me: StaffMe;
  can: (perm: string) => boolean;
  outletId: string | null;
  outlet: StaffMe['outlets'][number] | null;
  setOutletId: (id: string) => void;
  logout: () => Promise<void>;
  /** The super admin and the accountant work across the whole venue (no outlet of their own). */
  venueWide: boolean;
  /**
   * Venue-wide logins looking inside one shop ("All shops" → Open shop): the menu becomes that shop's own
   * menu and the header says which shop is open. Kept per browser tab.
   */
  viewingShop: boolean;
  openShop: (outletId: string) => void;
  exitShop: () => void;
  /** At a venue-run outlet the super admin may switch on billing ("Work at this counter"). */
  workAtCounter: boolean;
  setWorkAtCounter: (on: boolean) => void;
  /** The login must choose a new password before using anything else. */
  mustChangePassword: boolean;
}

const Ctx = createContext<StaffCtx | null>(null);
const OUTLET_KEY = 'z3.outlet';
const VIEW_KEY = 'z3.viewing-shop';
const COUNTER_KEY = 'z3.work-at-counter';

function readOutlet() {
  try {
    return localStorage.getItem(OUTLET_KEY);
  } catch {
    return null;
  }
}

// Drill-down state lives in sessionStorage: a new tab or a fresh sign-in starts on the venue menu.
function readFlag(key: string) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function writeFlag(key: string, on: boolean) {
  try {
    if (on) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode: the flag still holds until reload */
  }
}

/** Whether the server asked this login to replace its password (the field may sit on `me` or on `me.user`). */
export function mustChange(me: StaffMe | null | undefined) {
  return !!(me && (me.mustChangePassword || me.user.mustChangePassword || (me.user as { must_change_password?: boolean }).must_change_password));
}

export function useStaffMe() {
  return useQuery<StaffMe | null>({
    queryKey: ['staff-me'],
    queryFn: async () => {
      try {
        return await api<StaffMe>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          clearStoredToken();
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** Whether the device has a network connection right now (updates when it goes on or off line). */
export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * Logins are lowercase letters, digits, dots, dashes and underscores, never spaces. Phone keyboards add
 * stray spaces and capitalise the first letter, so both are taken out before signing in. The password is
 * always sent exactly as typed.
 */
export const normaliseLogin = (v: string) => v.replace(/\s+/g, '').toLowerCase();

/** What went wrong with a sign-in, in the few kinds a person can act on differently. */
export type LoginProblem =
  | { kind: 'wrong' }
  | { kind: 'disabled' }
  | { kind: 'deleted' }
  | { kind: 'shop_closed' }
  | { kind: 'locked'; until: Date | null }
  | { kind: 'slow'; seconds: number; deviceWide: boolean }
  | { kind: 'offline' }
  | { kind: 'server' }
  | { kind: 'other'; message: string };

export function loginProblem(err: unknown): LoginProblem | null {
  if (!err) return null;
  if (!(err instanceof ApiError)) return { kind: 'offline' };
  if (err.status === 423 || err.code === 'account_locked') {
    const iso = (err.details as { lockedUntil?: string } | undefined)?.lockedUntil ?? /(\d{4}-\d{2}-\d{2}T[\d:.]+Z)/.exec(err.message)?.[1];
    const d = iso ? new Date(iso) : null;
    return { kind: 'locked', until: d && !Number.isNaN(d.getTime()) ? d : null };
  }
  if (err.status === 429) {
    // login_slow_down: wrong passwords for this login from many devices (the server says how long to wait).
    // rate_limited: too many sign-in requests from this device in a minute.
    const n = Number((err.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds);
    const deviceWide = err.code !== 'login_slow_down';
    return { kind: 'slow', seconds: Number.isFinite(n) && n > 0 ? Math.ceil(n) : deviceWide ? 60 : 1, deviceWide };
  }
  if (err.status === 401 || err.status === 403) {
    if (err.code === 'account_deleted') return { kind: 'deleted' };
    if (err.code === 'shop_closed') return { kind: 'shop_closed' };
    if (err.code === 'account_disabled' || /deactivat|disabled|switched off/i.test(err.message)) return { kind: 'disabled' };
    return { kind: 'wrong' };
  }
  if (err.status >= 500) return { kind: 'server' };
  return { kind: 'other', message: errorMessage(err) };
}

/** Guards the staff portal and provides the signed-in user, permissions and selected outlet. */
export function StaffAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading, isError, error, refetch } = useStaffMe();
  const qc = useQueryClient();
  const location = useLocation();
  const [selected, setSelected] = useState<string | null>(readOutlet);
  const [viewing, setViewing] = useState(() => readFlag(VIEW_KEY));
  const [counter, setCounter] = useState(() => readFlag(COUNTER_KEY));
  const online = useOnline();
  // Back on line after a failed check: look again by itself instead of waiting for a tap on Retry.
  useEffect(() => {
    if (online && isError) void refetch();
  }, [online, isError, refetch]);

  const setOutletId = useCallback((id: string) => {
    setSelected(id);
    try {
      localStorage.setItem(OUTLET_KEY, id);
    } catch {
      /* private mode */
    }
  }, []);
  const setViewingFlag = useCallback((on: boolean) => {
    setViewing(on);
    writeFlag(VIEW_KEY, on);
  }, []);
  const setCounterFlag = useCallback((on: boolean) => {
    setCounter(on);
    writeFlag(COUNTER_KEY, on);
  }, []);

  const value = useMemo<StaffCtx | null>(() => {
    if (!me) return null;
    const outlet = me.outlets.find((o) => o.id === selected) ?? me.outlets[0] ?? null;
    const venueWide = me.user.role === 'owner' || me.user.role === 'accountant';
    const viewingShop = venueWide && viewing && outlet?.kind === 'shop';
    return {
      me,
      can: (perm) => me.permissions.includes(perm),
      outletId: outlet?.id ?? null,
      outlet,
      setOutletId,
      logout: async () => {
        // No new request may start from here on (only /auth calls go out), so nothing can reach the server
        // between the sign-out and the sign-in page and come back 401.
        markSignedOut();
        // The live stream stops too (its feed requests do not go through react-query).
        await stopLive();
        // Let requests already on the wire finish while the session is still valid, so none comes back 401.
        const started = Date.now();
        while (qc.isFetching() > 0 && Date.now() - started < 1500) await new Promise((r) => setTimeout(r, 25));
        await qc.cancelQueries();
        await api('/auth/logout', { method: 'POST', body: {} }).catch(() => {});
        clearStoredToken();
        // A shared gate phone keeps no other login's entries and no photos the server already has (security outbox).
        if (me.permissions.includes('visits.write')) {
          await import('../pages/staff/security/useOutbox').then((m) => m.forgetOutboxOnSignOut(me.user.id)).catch(() => undefined);
        }
        setViewingFlag(false);
        setCounterFlag(false);
        // The null "who am I" sends the shell to the sign-in page; the cache is cleared once those pages are gone.
        qc.setQueryData(['staff-me'], null);
        setTimeout(() => qc.clear(), 0);
      },
      venueWide,
      viewingShop,
      openShop: (id) => {
        if (id !== outlet?.id) setCounterFlag(false);
        setOutletId(id);
        setViewingFlag(true);
      },
      exitShop: () => {
        setViewingFlag(false);
        setCounterFlag(false);
        // Back on the venue menu, gate screens work on a vehicle gate again.
        const gate = me.outlets.find((o) => o.kind === 'parking');
        if (gate) setOutletId(gate.id);
      },
      workAtCounter: viewingShop && counter,
      setWorkAtCounter: setCounterFlag,
      mustChangePassword: mustChange(me),
    };
  }, [me, selected, viewing, counter, setOutletId, setViewingFlag, setCounterFlag, qc]);

  if (isLoading) return <FullPageSpinner />;
  // A failed re-check (Wi-Fi dropped for a moment) keeps the signed-in screens: only a first check that never
  // got an answer shows this page. A sign-in that was already over goes to the sign-in page, which works offline.
  if (isError && me === undefined) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-shell text-slate-800 text-center">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">!</div>
          <h2 className="text-lg font-bold text-slate-900">Unable to reach server</h2>
          <p className="text-sm text-slate-600">
            {online ? errorMessage(error) : 'This device is offline. The app opens again by itself as soon as the connection is back.'}
          </p>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg text-sm cursor-pointer"
            >
              Retry
            </button>
            <a
              href="/admin/login"
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg text-sm"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }
  if (!value) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStaff() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStaff outside StaffAuth');
  return v;
}

/** True when the login has any one of the permissions (a single name works too). */
export function canAny(can: (perm: string) => boolean, perm: string | readonly string[]) {
  return typeof perm === 'string' ? can(perm) : perm.some((p) => can(p));
}

/**
 * Hides a route from roles without the permission (any one of a list is enough). The fallback page must
 * be one the login can open, otherwise the two redirects would bounce forever.
 */
export function RequirePerm({ perm, children }: { perm: string | readonly string[]; children: ReactNode }) {
  const { can } = useStaff();
  if (!canAny(can, perm)) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

// ------------------------------------------------------------------ customer
export interface CustomerMe {
  profile: {
    id: string;
    name: string | null;
    mobile: string;
    email: string | null;
    place: string | null;
    district: string | null;
    state: string | null;
    pincode: string | null;
    marketing_consent: boolean;
    has_pin: boolean;
  };
  vehicles: { id: string; reg_display: string; vehicle_type: string }[];
  features: Features;
  business?: { name: string; address: string | null; supportPhone: string | null };
  walletBalancePaise: number | null;
  /** The owner's points system ("Points at every shop"): the app talks of points, and every spend is a bill the counter made. */
  pointsMode?: boolean;
  active_coupons: number;
  unread: number;
}

export function useCustomerMe() {
  return useQuery<CustomerMe | null>({
    queryKey: ['customer-me'],
    queryFn: async () => {
      try {
        return await api<CustomerMe>('/customer/me');
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          clearStoredToken();
          return null;
        }
        throw e;
      }
    },
    staleTime: 15_000,
  });
}

export function CustomerAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error, refetch } = useCustomerMe();
  const location = useLocation();
  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-shell text-slate-800 text-center">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">!</div>
          <h2 className="text-lg font-bold text-slate-900">Unable to reach server</h2>
          <p className="text-sm text-slate-600">{errorMessage(error)}</p>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg text-sm cursor-pointer"
            >
              Retry
            </button>
            <a
              href="/app/login"
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg text-sm"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <Navigate to="/app/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

export function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
    </div>
  );
}
